import { Modal, ModalBody, ModalHeader } from '@msdyn365-commerce-modules/utilities';
import { Address } from '@msdyn365-commerce/retail-proxy';
import classnames from 'classnames';
import { observable } from 'mobx';
import { observer } from 'mobx-react';
import moment from 'moment';
import * as React from 'react';
import { FormInputGroupBuilder, formInputGroupStructure } from '../../../components/input-group-form';
import { selectListItem } from '../../../components/select-input-group';
import { IrsmSubscriptionData } from '../../../dataActions/DataServiceEntities.g';
import { IGetSubscriptionCardsData } from '../actions/get-subscription-cards.action';
import { createSubscriptionStateAction, createSubscriptionStateInput, ISubscriptionStateData, state } from '../actions/subscription-state.action';
import { createSubscriptionUpdateInput, ISubscriptionUpdateData, subscriptionUpdateAction } from '../actions/subscription-update.action';
import { ISubscriptionManagementData } from '../subscription-management.data';
import { ISubscriptionManagementProps } from '../subscription-management.props.autogenerated';
import { getErrorCodeMap } from '../utility/error-code-map';
import { formatDate } from '../utility/format-date';
import { parseFrequency } from '../utility/frequency-parser';
import { SubscriptionStatusDisplay } from '../utility/sub-status';
import { SubscriptionConfirmationComponent } from './subscription-confirmation';

export interface ISubscriptionLine {
    line: IrsmSubscriptionData;
    cards: IGetSubscriptionCardsData;
    addresses: Address[];
    managementProps: ISubscriptionManagementProps<ISubscriptionManagementData>;
    onSuccess?(error: string): void;
    onError?(error: string): void;
}

/**
 * delcaration for a single subscription line on the management
 * module
 */
@observer
export class SubscriptionLine extends React.Component<ISubscriptionLine> {
    // limits the requests to one at a time
    @observable private _disableAll: boolean = false;

    @observable private _dropdownState: boolean = false;
    @observable private _editPopupState: boolean = false;

    @observable private _confirmationState: boolean = false;

    @observable private _itemNames: string[];

    @observable private _formError: string = '';

    private _currentPendingForm: 'pause' | 'resume' | 'cancel' | undefined;
    private _currentFormQuestion: string | undefined;
    private _refreshKey: number = 0;
    private _form: formInputGroupStructure;

    public constructor(props: ISubscriptionLine) {
        super(props);

        this._toggleDropdown = this._toggleDropdown.bind(this);

        this._toggleEditPopup = this._toggleEditPopup.bind(this);
        this._changeActionAccepted = this._changeActionAccepted.bind(this);
        this._changeDenied = this._changeDenied.bind(this);
        this._pauseAction = this._pauseAction.bind(this);
        this._resumeAction = this._resumeAction.bind(this);
        this._cancelAction = this._cancelAction.bind(this);

        this._onEditFinish = this._onEditFinish.bind(this);

        this._validateValue = this._validateValue.bind(this);

        this._form = this._loadForm();

        this._itemNames = this.props.line.Lines?.map(line => `${line.Description!} x${line.Qty!}`) || [];
    }

    public render(): JSX.Element {
        const { line } = this.props;
        const {
            resources: {
                subManagement_unavailableAction }, config: { frequencyTable }
        } = this.props.managementProps;

        return (
            <div className='subscription-management-line'>
                <div className={`subscription-management-status STATUS-TYPE-${line.SubStatus}`}>
                    {this._renderSubStatus(line.SubStatus)}
                </div>
                <div className='subscription-management-selection'>
                    <button
                        className={classnames('subscription-management-selection-button', { expanded: this._dropdownState }, { collapsed: !this._dropdownState })}
                        onClick={this._toggleDropdown}
                    >
                        {line.SubscriptionName}
                    </button>
                </div>
                <div className={classnames('subscription-management-selection-details', { expanded: this._dropdownState }, { collapsed: !this._dropdownState })}>
                    {this._dropdownState && this._itemNames?.map((itemName, key) => <div key={key}>{itemName}</div>)}
                </div>
                <div className='subscription-management-frequency'>
                    {parseFrequency(frequencyTable, line.Frequency)}
                </div>
                <div className='subscription-management-next-order-date'>
                    {formatDate(line.NextOrderDate)}
                </div>
                <div className='subscription-management-actions'>
                    {line.SubStatus === 'CANCELED' ? null : (
                        this._actionsEnabled ? this._renderActions() : `${subManagement_unavailableAction} ${formatDate(line.NextOrderDate)}`
                    )}
                </div>
                <SubscriptionConfirmationComponent
                    isOpen={this._confirmationState}
                    onAccept={this._changeActionAccepted}
                    onDeny={this._changeDenied}
                    resources={this.props.managementProps.resources}
                >
                    {this._currentFormQuestion}
                </SubscriptionConfirmationComponent>
                <Modal
                    autoFocus={true}
                    fade={true}
                    isOpen={this._editPopupState}
                    horizontalPosition='center'
                    verticalPosition='center'
                    zIndex={1000}
                    toggle={this._toggleEditPopup}
                    applicationNode='rsg-root'
                    modalClassName='subscription-management-modal'
                >
                    <ModalHeader toggle={this._toggleEditPopup} />
                    <ModalBody>
                        {this._formError && <div className='alert alert-danger text-input-validation-errors'>{this._formError}</div>}
                        <FormInputGroupBuilder
                            key={this._refreshKey}
                            form={this._form}
                            onFinish={this._onEditFinish}
                            onFieldValidityCheck={this._validateValue}
                        />
                    </ModalBody>
                </Modal>
            </div>
        );
    }

    private _renderSubStatus(status?: string): string | undefined {
        return status && SubscriptionStatusDisplay[status];
    }

    // Returns the earliest possible ship date, in YYYY-MM-DD string format
    private _earliestShipDate(): string {
        const { LockoutPeriod } = this.props.line;

        // tslint:disable-next-line: prefer-const
        const date = moment(moment.now());
        date.add(LockoutPeriod || 5, 'days');

        return date.format('YYYY-MM-DD');
    }

    private _validateValue(valueToValidate: string, id: string): boolean {
        if (id === 'nextShipment') {
            const now = moment(moment.now());
            const mDate = moment(valueToValidate);
            return mDate.isValid() && now.isBefore(mDate);
        } else if (id === 'subscriptionName') {
            return valueToValidate.length > 0;
        }
        return true;
    }

    private _renderActions(): JSX.Element {
        const { SubStatus } = this.props.line;

        return (
            <React.Fragment>
                <button onClick={this._toggleEditPopup} disabled={this._disableAll} className='subscription-action-edit' />
                {(SubStatus === 'ACTIVE') && <button onClick={this._pauseAction} disabled={this._disableAll} className='subscription-action-pause' />}
                {(SubStatus === 'PAUSED') && <button onClick={this._resumeAction} disabled={this._disableAll} className='subscription-action-start' />}
                <button onClick={this._cancelAction} disabled={this._disableAll} className='subscription-action-cancel' />
            </React.Fragment>
        );
    }

    private async _onEditFinish(data: { frequency: string; nextShipment: string; shippingAddress: string; creditCard: string; subscriptionName: string }): Promise<void> {
        const context = this.props.managementProps.context;
        let response: ISubscriptionUpdateData | undefined;
        try {
            response = await subscriptionUpdateAction(
                createSubscriptionUpdateInput(
                    context.actionContext,
                    {
                        addressId: data.shippingAddress,
                        cardId: data.creditCard,
                        nextOrderDate: data.nextShipment,
                        subscriptionName: data.subscriptionName,
                        frequency: data.frequency,
                        subscriptionId: this.props.line.SubscriptionID!
                    }
                ),
                context.actionContext
            );
        } catch (e) {
            console.error(e);
            this._formError = this.props.managementProps.resources.subManagement_errProcess;
        }
        if (response) {
            this._formError = this._interpretResponse(response.Status!) || '';
            if (!this._formError) {
                this._editPopupState = false;
                this.props.onSuccess && this.props.onSuccess(this.props.managementProps.config.success?.edit || '');
                this.props.managementProps.data.subscriptions.result!.Result = response.Result;
                this.props.managementProps.data.subscriptions.run(res => res({ Result: response?.Result }));
            }
        } else {
            this._formError = this.props.managementProps.resources.subManagement_errProcess;
        }
    }

    private async _changeActionAccepted(): Promise<void> {
        this._confirmationState = false;
        this._disableAll = true;
        this._currentPendingForm && await this._doAction(this._currentPendingForm, this.props.managementProps.config.success?.[this._currentPendingForm] || '');
        this._disableAll = false;
    }

    private _changeDenied(): void {
        this._confirmationState = false;
        this._currentPendingForm = undefined;
    }

    private _toggleEditPopup(): void {
        this._editPopupState = !this._editPopupState;
        this._formError = '';
        // allows the modal to competely be refreshed
        ++this._refreshKey;
    }
    private async _pauseAction(): Promise<void> {
        this._currentPendingForm = 'pause';
        this._currentFormQuestion = this.props.managementProps.config.confirmationMessages?.[this._currentPendingForm];
        this._confirmationState = true;
    }
    private async _resumeAction(): Promise<void> {
        this._currentPendingForm = 'resume';
        this._currentFormQuestion = this.props.managementProps.config.confirmationMessages?.[this._currentPendingForm];
        this._confirmationState = true;
    }
    private async _cancelAction(): Promise<void> {
        this._currentPendingForm = 'cancel';
        this._currentFormQuestion = this.props.managementProps.config.confirmationMessages?.[this._currentPendingForm];
        this._confirmationState = true;
    }

    private async _doAction(newState: state, ifSuccess: string): Promise<void> {
        let response: ISubscriptionStateData | undefined;
        try {
            response = await this._constructStateRequest(newState);
        } catch (e) {
            console.error(e);
            this.props.onError && this.props.onError(this.props.managementProps.resources.subManagement_errProcess);
        }
        if (response) {
            const containsError = this._interpretResponse(response.Status!);
            if (containsError) {
                this.props.onError && this.props.onError(containsError);
            } else {
                this.props.managementProps.data.subscriptions.result!.Result = response.Result;
                this.props.managementProps.data.subscriptions.run(res => res({ Result: response?.Result }));
                this.props.onSuccess && this.props.onSuccess(ifSuccess);
            }
        }

    }

    private async _constructStateRequest(newState: state): Promise<ISubscriptionStateData> {
        const { context } = this.props.managementProps;
        return createSubscriptionStateAction(
            createSubscriptionStateInput(
                context.actionContext,
                {
                    customerId: context.request.user.customerAccountNumber!,
                    subscriptionId: this.props.line.SubscriptionID!,
                    newState: newState
                }
            ),
            context.actionContext
        );
    }

    private _interpretResponse(status: string): void | string {
        const map = getErrorCodeMap(this.props.managementProps.resources);
        const error = map[status];
        if (error) { return error; }
    }

    private get _actionsEnabled(): boolean {
        const { NextOrderDate, LockoutPeriod } = this.props.line;
        const nextOrderDate = moment(NextOrderDate);
        const blackoutStart = moment(nextOrderDate).subtract(LockoutPeriod, 'days');
        const now = moment();
        return now.isBefore(blackoutStart, 'day') || now.isAfter(nextOrderDate, 'day');
    }

    private _toggleDropdown(): void {
        this._dropdownState = !this._dropdownState;
    }

    private _loadForm(): formInputGroupStructure {
        const { line, cards, addresses } = this.props;
        const {
            resources: {
                subManagement_editFrequency,
                subManagement_editShipmentDate,
                subManagement_editCreditCard,
                subManagement_editShipmentDateInvalid,
                subManagement_editShipmentDateWarningStart,
                subManagement_editShipmentDateWarningEnd,
                subManagement_editShippingAddress,
                subManagement_editName,
                subManagement_errInvalidName
            }, config: { frequencyTable }
        } = this.props.managementProps;

        return {
            subscriptionName: {
                validity: true,
                value: line.SubscriptionName!,
                externalGroup: {
                    labelText: subManagement_editName,
                    validationText: subManagement_errInvalidName
                }
            },
            frequency: {
                validity: true,
                value: line.Frequency!,
                externalGroup: { labelText: subManagement_editFrequency },
                selectList: frequencyTable?.map(frequency => { return { label: frequency.freqLabel, id: frequency.freqId }; }) as selectListItem[]
            },
            nextShipment: {
                validity: true,
                useDatePicker: true,
                minDate: this._earliestShipDate(),
                value: line.NextOrderDate!,
                externalGroup: {
                    labelText: subManagement_editShipmentDate,
                    validationText: subManagement_editShipmentDateInvalid
                },
                customRender: inputJSX => {
                    return (
                        <>
                            {inputJSX}
                            <div className='subscription-management-modal-shipping-text'>
                                {`${subManagement_editShipmentDateWarningStart} ${line.LockoutPeriod || '5'} ${subManagement_editShipmentDateWarningEnd}`}
                            </div>
                        </>
                    );
                }
            },
            shippingAddress: {
                validity: true,
                value: line.DeliveryAddress!,
                externalGroup: { labelText: subManagement_editShippingAddress },
                selectList: addresses.map(address => { return { label: address.FullAddress, id: `${address.RecordId!}` }; }) as selectListItem[]
            },
            creditCard: {
                validity: true,
                value: line.ActiveCreditCardID!,
                externalGroup: { labelText: subManagement_editCreditCard },
                selectList: cards.Result?.map(card => { return { label: card.CardNumber, id: card.CardID }; }) as selectListItem[]
            }
        };
    }
}